import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import UpdateCard from './UpdateCard';
import { TeamHubPostView } from '../../../models/preformanceManagement';

interface UpdatesFeedProps {
  posts: TeamHubPostView[];
  loading: boolean;
  filters?: {
    dateRange: string;
    updateType: string;
    department: string;
    searchQuery: string;
  };
  searchQuery?: string;
  currentUserId?: string;
  onDeletePost: (postId: number) => void;
  onAddComment: (postId: number, comment: string) => void;
  onDeleteComment: (commentId: number, postId: number) => void;
  onRefresh: () => void;
}

const UpdatesFeed: React.FC<UpdatesFeedProps> = ({
  posts = [],
  loading = false,
  filters = {
    dateRange: '',
    updateType: '',
    department: '',
    searchQuery: ''
  },
  searchQuery = '',
  currentUserId,
  onDeletePost,
  onAddComment,
  onDeleteComment,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [filteredPosts, setFilteredPosts] = useState<TeamHubPostView[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Apply filters to posts
  useEffect(() => {
    if (!posts || posts.length === 0) {
      setFilteredPosts([]);
      return;
    }

    let filtered = [...posts];

    // Apply search filter
    if (searchQuery?.trim()) {
      filtered = filtered?.filter((post) =>
        post?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        post?.authorName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        post?.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply type filter
    if (filters?.updateType && filters?.updateType !== 'all' && filters?.updateType !== '') {
      filtered = filtered?.filter((post) => post?.postType === filters?.updateType);
    }

    // Apply date range filter
    if (filters?.dateRange) {
      const now = new Date();
      const filterDate = new Date();

      switch (filters?.dateRange) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          filtered = filtered?.filter((post) => new Date(post.postedAt) >= filterDate);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          filtered = filtered?.filter((post) => new Date(post.postedAt) >= filterDate);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          filtered = filtered?.filter((post) => new Date(post.postedAt) >= filterDate);
          break;
        case 'quarter':
          filterDate.setMonth(now.getMonth() - 3);
          filtered = filtered?.filter((post) => new Date(post.postedAt) >= filterDate);
          break;
        default:
          break;
      }
    }

    setFilteredPosts(filtered);
  }, [posts, filters, searchQuery]);

  const loadMoreUpdates = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setLoadingMore(false);
      setHasMore(false);
    }, 1000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px] w-full">
        <Spinner />
      </div>
    );
  }

  if (filteredPosts.length === 0 && !loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center w-full">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('performance.teamHub.noUpdatesFound')}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {searchQuery || Object.values(filters).some(f => f && f !== 'all' && f !== '') ?
            t('performance.teamHub.tryAdjustingFilters') :
            t('performance.teamHub.beFirstToShare')}
        </p>
        <Button
          variant="outline"
          iconName="RotateCcw"
          iconPosition="left"
          onClick={onRefresh}
        >
          {t('performance.teamHub.refresh')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Posts List */}
      {filteredPosts.map((post) => (
        <UpdateCard
          key={post.id}
          update={{
            id: post.id,
            content: post.content,
            type: post.postType || 'update',
            milestone: post.postType === 'milestone' ? post.postType : '',
            attachments: post.attachmentUrls?.map((url, index) => ({
              id: index + 1,
              name: url.split('/').pop() || `Attachment ${index + 1}`,
              size: 0,
              type: url.match(/\.(pdf|docx?|xlsx?|jpg|jpeg|png|gif)$/i)?.[1] || 'unknown'
            })) || [],
            timestamp: new Date(post.postedAt),
            author: {
              name: post.authorName,
              role: '',
              avatar: post.authorAvatar,
              avatarAlt: post.authorName
            },
            department: post.department || 'general'
          }}
          isAuthor={post.authorId === currentUserId}
          comments={post.comments || []}
          onComment={(comment) => onAddComment(post.id, comment)}
          onDelete={() => onDeletePost(post.id)}
          onDeleteComment={(commentId) => onDeleteComment(commentId, post.id)}
          canDeleteComment={(commentAuthorId) =>
            commentAuthorId === currentUserId || post.authorId === currentUserId
          }
        />
      ))}

      {/* Load More Button */}
      {hasMore && filteredPosts.length > 0 && (
        <div className="text-center pt-3">
          <Button
            variant="outline"
            onClick={loadMoreUpdates}
            disabled={loadingMore}
            loading={loadingMore}
          >
            {loadingMore ? t('performance.teamHub.loading') : t('performance.teamHub.loadMoreUpdates')}
          </Button>
        </div>
      )}

      {/* End of Feed */}
      {!hasMore && filteredPosts.length > 0 && (
        <div className="text-center pt-3 pb-2">
          <p className="text-sm text-muted-foreground">
            {t('performance.teamHub.endOfFeed')}
          </p>
        </div>
      )}
    </div>
  );
};

export default UpdatesFeed;